import { useEffect, useState } from "react"
import { useLocation } from "react-router-dom"

import { cn } from "@/lib/utils"

import NavbarNode from "./NavbarNode"
import { isNavNodeOrChildActive, TNavbarNode } from "./utils"

const findActiveParentHref = (nodes: TNavbarNode[], pathname: string) =>
  nodes.find((node) => node.children && isNavNodeOrChildActive(node, pathname))?.href ?? null

export default function NavbarSection({
  heading,
  collapsedHeading,
  nodes,
  isBordered,
  isNavbarCollapsed,
  countOffset,
}: {
  heading: string
  /** Shorter heading shown when the sidebar is collapsed; falls back to `heading` when omitted. */
  collapsedHeading?: string
  nodes: TNavbarNode[]
  isBordered: boolean
  isNavbarCollapsed: boolean
  countOffset: number
}) {
  const { pathname } = useLocation()

  // Only one submenu can stay open per section at a time. Defaults to whichever parent contains
  // the current route so the correct accordion is open on first load and after a refresh.
  const [expandedHref, setExpandedHref] = useState<string | null>(() => findActiveParentHref(nodes, pathname))

  // Re-syncs the open accordion to whichever parent contains the active route, whether that's
  // triggered by navigating to a child or by `nodes` changing shape (e.g. useRoutes() rebuilding
  // the tree once an async-loaded flag like scope1Blocked resolves), without disturbing manually
  // opened/closed accordions in unrelated sections.
  useEffect(() => {
    const activeParentHref = findActiveParentHref(nodes, pathname)
    if (activeParentHref) {
      setExpandedHref(activeParentHref)
    }
  }, [pathname, nodes])

  return (
    // Collapsed rail: the section gains 4px each side (-mx-1) and pads it back (px-1), so the items
    // keep their 64px box while the heading can use 72px. With tracking-tight, the longest single-word
    // headings (ADVERTISING, MUNICIPALITY, COMMUNITY, COMUNIDAD) then fit without clipping.
    <section className={cn("mb-2 flex flex-col gap-1 overflow-hidden text-nowrap", isNavbarCollapsed && "-mx-1 px-1")}>
      <div className={cn("transition-all duration-700", isBordered || isNavbarCollapsed ? "pt-2" : "pt-0")}>
        <h6
          className={cn(
            "text-metadata font-medium uppercase text-neutral-text transition-all duration-700",
            isNavbarCollapsed
              ? "-mx-1 block whitespace-normal text-center leading-tight !tracking-tight"
              : "mx-2 inline-block text-nowrap",
          )}
        >
          {isNavbarCollapsed ? (collapsedHeading ?? heading) : heading}
        </h6>
      </div>
      {nodes.map((node, i) => (
        <NavbarNode
          key={node.href}
          isNavbarCollapsed={isNavbarCollapsed}
          count={countOffset + i}
          node={node}
          isExpanded={!!node.children && expandedHref === node.href}
          onToggle={() => setExpandedHref((prev) => (prev === node.href ? null : node.href))}
        />
      ))}
    </section>
  )
}
